import React, { useState, useEffect, useCallback } from "react"
import axios from "axios"
import { Typography, Grid, Card, Dialog, DialogTitle, DialogContent, IconButton, Box, Paper, Stack, Tooltip, Toolbar } from "@mui/material"
import { useAuth } from "../../../../context/AuthContext"
import { useAppData } from "../../../../context/AppDataContext"
import { toast } from "react-toastify"
import CloseIcon from "@mui/icons-material/Close"
import dayjs from "dayjs"
import { DatePicker } from "@mui/x-date-pickers"
import { motion } from "framer-motion"
import Tile from "./Tile"

import DevicesIcon from "@mui/icons-material/Devices"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import PaidIcon from "@mui/icons-material/Paid"
import FlashOnIcon from "@mui/icons-material/FlashOn"
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone"
import HeadphonesIcon from "@mui/icons-material/Headphones"
import { Skeleton } from "@mui/material"
import useMediaQuery from "@mui/material/useMediaQuery"

import "react-circular-progressbar/dist/styles.css"
import BreakdownSheet from "./BreakdownSheet"

const getKpiColor = (label, value) => {
  switch (label) {
    case "Bill Payment":
      return value >= 1000 ? "#52C41A" : "#FAAD14"
    case "Accessory Sales":
      return value >= 500 ? "#1890FF" : "#FF4D4F"
    case "Device Setup":
      return value >= 50 ? "#52C41A" : "#FAAD14"
    default:
      return "#0d1b3d"
  }
}

const EmployeeIncentiveBreakDown = ({
  isDateChanger = true,
  employeeId,
  clientId,
  month: propMonth,
  year: propYear,
  rows: propRows,
  total: propTotal,
  isModal = false,
  open = false,
  onClose,
  title,
  fullWidthParent,
  isSmall,
}) => {
  const { authToken } = useAuth()
  const { baseApi } = useAppData()

  const currentDayjs = dayjs()

  const [sheetInfo, setSheetInfo] = useState({ open: false })
  const [selectedMonth, setSelectedMonth] = useState(propMonth ? dayjs(`${propYear}-${propMonth}-01`) : currentDayjs)

  const [selectedYear, setSelectedYear] = useState(propYear ? dayjs(`${propYear}-01-01`) : currentDayjs)
  const [availableMonths, setAvailableMonths] = useState([])

  const [loading, setLoading] = useState(true)
  const [detailRows, setDetailRows] = useState(propRows || [])
  const [totalIncentive, setTotalIncentive] = useState(propTotal || 0)
  const [selectedRow, setSelectedRow] = useState(null)
  const [columns, setColumns] = useState([])
  const [dsrSummary, setDsrSummary] = useState(null)

  const [dsrMode, setDsrMode] = useState("mtd") // "mtd" | "today"
  const isTablet = useMediaQuery("(max-width:876px)")

  // If rows came from props → treat as initial load data
  const handleOpenBreakdown = async (rule) => {
    let month = selectedMonth ? selectedMonth.month() + 1 : null
    let year = selectedYear.year()
    let date = dayjs().date()

    const currentMonth = dayjs().month() + 1
    const currentYear = dayjs().year()
    let currentDate = dayjs().date()

    if (dsrMode === "mtd") {
      date = "null"
    } else {
      month = currentMonth
      year = currentYear
      date = currentDate
    }

    setSheetInfo({
      open: true,
      clientId: clientId,
      employeeId: employeeId,
      month: month,
      year: year,
      selectedDate: date,
      ruleName: rule?.rule_name || "all",
    })
  }

  const handleSheetClose = () => {
    setSheetInfo({
      open: false,
    })
  }

  const fetchAvailableMonths = useCallback(
    async (_year) => {
      if (!clientId) return
      try {
        const response = await axios.get(`${baseApi}/sale/available-months/${clientId}/${_year}`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "ngrok-skip-browser-warning": "69420",
          },
        })
        setAvailableMonths(response.data || [])
      } catch (e) {
        console.error("Failed to fetch months", e)
      }
    },
    [clientId, baseApi, authToken]
  )

  const fetchEmployeeIncentives = useCallback(async () => {
    const month = selectedMonth ? selectedMonth.month() + 1 : null
    const year = selectedYear.year()

    const currentMonth = dayjs().month() + 1
    const currentYear = dayjs().year()
    const currentDate = dayjs().date()

    try {
      let url = ``

      if (dsrMode === "mtd") {
        // Month-to-date
        url = `${baseApi}/employee/incentives/${clientId}/${employeeId}/${month}/${year}/null`
      } else {
        // EXACT DATE (today or selected)
        url = `${baseApi}/employee/incentives/${clientId}/${employeeId}/${currentMonth}/${currentYear}/${currentDate}`
      }

      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${authToken}` },
      })

      setDetailRows(data.records || [])
      setTotalIncentive(data.totalIncentive || 0)
      setSelectedRow({ id: employeeId, ...data.employee })

      if (data.records.length) {
        setColumns(
          Object.keys(data.records[0]).map((key) => ({
            field: key,
            headerName: key,
          }))
        )
      }
    } catch (err) {
      console.error(err)
      toast.error("Failed to fetch employee incentives.")
    } finally {
    }
  }, [dsrMode, employeeId, clientId, selectedMonth, selectedYear, baseApi, authToken])

  const fetchDsrSummary = useCallback(
    async (employeeId, date) => {
      try {
        let url = ""

        if (dsrMode === "mtd") {
          // Month-to-date
          const monthStr = dayjs(date).format("YYYY-MM")
          url = `${baseApi}/report/dsr/${clientId}/employee/${employeeId}/monthToDate/${monthStr}`
        } else {
          // EXACT DATE (today or selected)
          const dateStr = dayjs().format("YYYY-MM-DD")
          url = `${baseApi}/report/dsr/${clientId}/employee/${employeeId}/date/${dateStr}`
        }

        const { data } = await axios.get(url, {
          headers: { Authorization: `Bearer ${authToken}` },
        })

        if (data.success) setDsrSummary(data.summary)
      } catch (err) {
        console.error("Failed to fetch DSR summary", err)
        toast.error("Failed to fetch DSR summary.")
      }
    },
    [dsrMode, baseApi, clientId, authToken]
  )

  // useEffect(() => {
  //   setData()
  // }, [])

  const setData = async () => {
    // if (!dataCameFromProps) {
    if (employeeId == null || employeeId == undefined || !clientId || !selectedYear) return

    setLoading(true)
    const dateStr = selectedMonth.format("YYYY-MM")
    let [a1, b2] = await Promise.all([fetchEmployeeIncentives(), fetchDsrSummary(employeeId, dateStr)])
    setLoading(false)
    // }
  }

  useEffect(() => {
    fetchAvailableMonths(selectedYear.year())
    setData()
    // if (!dataCameFromProps && employeeId && clientId) {
    //   const date = dsrMode === "mtd" ? selectedMonth : dayjs() // today

    //   fetchEmployeeIncentives()
    //   fetchDsrSummary(employeeId, date)
    // }
  }, [selectedMonth, selectedYear, dsrMode])

  const incentivePercent = Math.min((totalIncentive / Math.max(totalIncentive, 1000)) * 100, 100)

  const renderContent = () => (
    <Box>
      {/* Month + Year selection header */}
      {isDateChanger && (
        <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "flex-start", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <DatePicker
              label="Month"
              views={["month"]}
              value={selectedMonth}
              onChange={(v) => setSelectedMonth(v)}
              disabled={dsrMode === "today"}
              shouldDisableMonth={(date) => {
                const m = date.month() + 1
                return !availableMonths.includes(m)
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  sx: { width: 150 },
                },
              }}
            />

            <DatePicker
              label="Year"
              views={["year"]}
              value={selectedYear}
              onChange={(v) => {
                setSelectedYear(v)
                setSelectedMonth(null)
                fetchAvailableMonths(v.year())
              }}
              disabled={dsrMode === "today"}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  sx: { width: 150 },
                },
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }} />

          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Paper
              onClick={() => setDsrMode("mtd")}
              sx={{
                px: 2,
                py: 1,
                cursor: "pointer",
                borderRadius: 2,
                background: dsrMode === "mtd" ? "var(--va-primary-color)" : "var(--va-secondary-bg)",
                color: dsrMode === "mtd" ? "white" : "var(--va-text-primary-color)",
                boxShadow: dsrMode === "mtd" ? "0 4px 12px rgba(0,0,0,0.2)" : "none",
                fontWeight: 600,
              }}
            >
              Month to Date
            </Paper>

            <Paper
              onClick={() => setDsrMode("today")}
              sx={{
                px: 2,
                py: 1,
                cursor: "pointer",
                borderRadius: 2,
                background: dsrMode === "today" ? "var(--va-primary-color)" : "var(--va-secondary-bg)",
                color: dsrMode === "today" ? "white" : "var(--va-text-primary-color)",
                boxShadow: dsrMode === "today" ? "0 4px 12px rgba(0,0,0,0.2)" : "none",
                fontWeight: 600,
              }}
            >
              Today
            </Paper>
          </Box>

          {/* SUMMARY CARDS */}
        </Box>
      )}

      {loading ? (
        <div>
          {" "}
          <Box>
            {/* Header Skeletons */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Grid container spacing={2} sx={{ width: "100%" }}>
                {[0, 1, 2].map(() => (
                  <Grid
                    item
                    size={{
                      xs: 8,
                      sm: 8,
                      md: 6,
                      lg: 6,
                      xl: 4,
                    }}
                    sx={{ display: "block" }}
                  >
                    <Skeleton variant="rounded" animation="wave" height={100} />
                  </Grid>
                ))}
                {[0, 1, 2, 3, 4, 5].map(() => (
                  <Grid
                    item
                    size={{
                      xs: 6,
                      sm: 6,
                      md: 4,
                      lg: 4,
                      xl: 2,
                    }}
                    sx={{ display: "block" }}
                  >
                    <Skeleton variant="rounded" animation="wave" height={100} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Stack direction="column" spacing={1}>
              {[0, 1].map((row, idx) => (
                <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                  <Skeleton variant="rounded" animation="wave" height={100} />
                </motion.div>
              ))}
            </Stack>
          </Box>
        </div>
      ) : (
        detailRows &&
        dsrSummary && (
          <>
            {/* SR SUMMARY CARDS */}
            <Grid container spacing={2} sx={{ mb: isSmall ? 2 : 3 }}>
              {/* Total Box */}

              <Tile
                fullWidthParent={fullWidthParent}
                title="Total Box"
                value={dsrSummary.total_box}
                icon={<DevicesIcon sx={{ fontSize: 20, color: "#5468FF" }} />}
                expandable
                secondary={() => (
                  <Toolbar sx={{ minHeight: "20px !important", gap: "10px", alignItems: "center", justifyContent: "center" }}>
                    <TrendingUpIcon sx={{ fontSize: 28, color: "#00A76F" }} />
                    <Typography variant="subtitle2" sx={{ color: "#5468FF", fontWeight: 600 }}>
                      {"Trending Box"}:
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {dsrSummary?.trending_box || 0}
                    </Typography>
                  </Toolbar>
                )}
              >
                <Box sx={{ display: "flex", flexDirection: "row", gap: "10px", justifyContent: "space-around" }}>
                  {[
                    { label: "New Line", value: dsrSummary?.total_activation },
                    { label: "Upgrade", value: dsrSummary?.upgrade },
                    { label: "Swap", value: dsrSummary?.swap },
                  ].map((i, idx) => (
                    <Box key={idx} sx={{ textAlign: "center" }}>
                      <Typography variant="caption">{i.label}</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {i.value || 0}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Tile>

              <Tile fullWidthParent={fullWidthParent} title="New Line" value={dsrSummary?.total_activation} icon={<FlashOnIcon sx={{ fontSize: 28, color: "#5468FF" }} />} expandable>
                <Box sx={{ display: "flex", gap: "10px", justifyContent: "space-around" }}>
                  {[
                    { label: "Activation", value: dsrSummary?.activation },
                    { label: "Port", value: dsrSummary?.port },
                    { label: "BYOD", value: dsrSummary?.byod },
                  ].map((i, idx) => (
                    <Box key={idx} sx={{ textAlign: "center" }}>
                      <Typography variant="caption">{i.label}</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {i.value || 0}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Tile>

              {/* Bill Payment */}
              <Tile fullWidthParent={fullWidthParent} title="Bill Payment" value={dsrSummary?.bill_payment_qty} icon={<PaidIcon sx={{ fontSize: 28, color: "#AB47BC" }} />}>
                <Typography
                  variant="caption"
                  sx={{
                    color: getKpiColor("Bill Payment", dsrSummary.bill_payment_amount || 0),
                    mt: 0.5,
                  }}
                >
                  Amount: ${dsrSummary.bill_payment_amount?.toFixed(2) || 0}
                </Typography>
              </Tile>

              <Tile
                title="Accessory Sales"
                fullWidthParent={fullWidthParent}
                value={dsrSummary?.accessory_sales}
                color={() => getKpiColor("Accessory Sales", dsrSummary.accessory_sales || 0)}
                icon={<HeadphonesIcon sx={{ fontSize: 20, color: "#3E8BFF" }} />}
                isAmount={true}
              >
                <Toolbar sx={{ minHeight: "20px !important", gap: "10px", alignItems: "center", justifyContent: "center" }}>
                  <TrendingUpIcon sx={{ fontSize: 20, color: "#00A76F" }} />
                  <Typography variant="caption" sx={{ fontSize: 14, color: "#5468FF", fontWeight: 600 }}>
                    {"Trending Sales"}:
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: 14 }}>
                    ${dsrSummary.trending_accessory_sales?.toFixed(2) || 0}
                  </Typography>
                </Toolbar>
              </Tile>

              <Tile
                fullWidthParent={fullWidthParent}
                title="Conversion"
                isCircular={true}
                value={dsrSummary.conversion?.toFixed(1) || 0}
                icon={<TrendingUpIcon sx={{ fontSize: 28, color: "#004FFF" }} />}
              ></Tile>

              <Tile fullWidthParent={fullWidthParent} title="Device Setup" value={dsrSummary?.device_setup} icon={<PhoneIphoneIcon sx={{ fontSize: 28, color: "#7B61FF" }} />} />

              {/* <Tile fullWidthParent={fullWidthParent} title="ACP" value={dsrSummary.acp} isAmount={true} />
              <Tile fullWidthParent={fullWidthParent} title="ABP" value={dsrSummary.abp} isAmount={true} /> */}
            </Grid>

            {/* INCENTIVE RULE CARDS */}
            <Box sx={{ maxHeight: isSmall ? "255px" : "", overflowY: "auto", padding: isSmall ? "10px 20px" : "20px" }}>
              {selectedRow && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 200, damping: 25 }}>
                  <Tooltip title="View all incentives">
                    <Box
                      onClick={() =>
                        setSheetInfo({
                          open: true,
                          clientId: clientId,
                          employeeId: employeeId,
                          month: selectedMonth ? selectedMonth.month() + 1 : null,
                          year: selectedYear.year(),
                          selectedDate: dsrMode === "mtd" ? "null" : dayjs().date(),
                          ruleName: "all", // Always "all" for total
                        })
                      }
                      sx={{
                        width: 200,
                        height: 30,
                        // borderRadius: "16px",
                        // bgcolor: "#4e79ff",
                        borderRadius: 2,
                        bgcolor: "var(--va-primary-color)",

                        color: "white",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-around",
                        alignSelf: "flex-end",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        mb: 2,
                        cursor: "pointer",
                        "&:hover": {
                          transform: "scale(1.02)",
                          boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
                        },
                      }}
                    >
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Total Incentives
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        $ {totalIncentive?.toFixed(0)}
                      </Typography>
                    </Box>
                  </Tooltip>
                </motion.div>
              )}
              <Stack direction="column" spacing={1}>
                {detailRows.map((row, idx) => (
                  <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                    <Tooltip title={`Earned: $${row.incentive}`}>
                      <Paper
                        onClick={() => handleOpenBreakdown(row)}
                        sx={{
                          py: isSmall ? 1 : 2,
                          px: 2,
                          borderRadius: 2,
                          background: "linear-gradient(135deg, #f7f7f7, #e1f0ff)",
                          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                          cursor: "pointer",
                          "&:hover": {
                            transform: "scale(1.02)",
                            boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                          },
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 600, color: "#0d1b3d", fontSize: isSmall ? "16px" : "" }}>
                            {row.rule_name} ({row.payout_tier})
                          </Typography>
                          <Typography sx={{ color: "#0d1b3d", mt: 0.3, fontSize: isSmall ? "14px" : "" }}>
                            Payout: ${row.payout_value} | Total Rule Value: {row.total_rule_value}
                          </Typography>
                        </Box>

                        <Paper
                          sx={{
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            background: "linear-gradient(135deg, #dff8e1, #a1ffb7)",
                            fontWeight: 700,
                            color: "#2e7d32",
                          }}
                        >
                          ${row.incentive}
                        </Paper>
                      </Paper>
                    </Tooltip>
                  </motion.div>
                ))}
              </Stack>
            </Box>

            <BreakdownSheet onClose={handleSheetClose} sheetInfo={sheetInfo} />
          </>
        )
      )}

      {/* <DSRSummaryBoard summary={dsrSummary} /> */}

      {/* EMPLOYEE HEADER */}

      {/* TABLE FALLBACK */}
      {/* <Box sx={{ mt: 2 }}>
            <ReconciliationTable maxHeight="350px" isAccordion={false} isExpanded={true} columns={columns} rows={detailRows} />
          </Box> */}
    </Box>
  )

  if (isModal) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            background: "rgba(255,255,255,0.08)",
            backdropFilter: "blur(16px)",
            borderRadius: 3,
            border: "1px solid rgba(255,255,255,0.15)",
            boxShadow: "0 12px 28px rgba(0,0,0,0.2)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            background: "rgba(84,104,255,0.08)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid rgba(84,104,255,0.2)",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#0d1b3d" }}>
            {title || "Employee Incentives"}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "#0d1b3d" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>{renderContent()}</DialogContent>
      </Dialog>
    )
  }

  // Card version
  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 3,
        background: "linear-gradient(135deg, #ffffff, #f1f7ff)",
        border: "1px solid rgba(84,104,255,0.2)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
      }}
    >
      {title && (
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: "#0d1b3d" }}>
          {title}
        </Typography>
      )}

      {renderContent()}
    </Card>
  )
}

export default EmployeeIncentiveBreakDown
